import { t } from 'ember-intl';
import { not } from 'tio-ui/utilities';
import Button from 'tio-ui/components/button';
import RadioGroup from 'tio-ui/components/radio-group';
import type { RadioOption } from 'tio-ui/components/radio-group';
import type { TOC } from '@ember/component/template-only';

interface LpnSurveyCardSignature {
  Args: {
    options: RadioOption[];
    selected: string | undefined;
    onSelect: (value: string) => void;
    onSubmit: () => void;
    isSubmitting: boolean;
    submitted: boolean;
  };
  Element: HTMLDivElement;
}

/**
 * The "did you get the LPN discount?" prompt shown after an application is
 * submitted, swapping to a thank-you once answered.
 *
 * Extracted from the confirmation route template so the manage screen's submitted
 * modal renders the identical card. Whether it appears at all is
 * `utils/tas/lpn-survey`'s call, shared by both surfaces; this component only
 * draws it.
 *
 * Presentational by design — the caller owns the selection, the save task and the
 * submitted flag, because the two surfaces persist it against the same record but
 * live in different components.
 */
const LpnSurveyCard: TOC<LpnSurveyCardSignature> = <template>
  <div class="tio-surface-sunken p-6 my-6 w-full max-w-xl text-left" ...attributes>
    {{#if @submitted}}
      <h4 class="tio-text-lg-semibold mb-1">
        {{t "tuition_assistance.submitted.lpn_survey.thanks_title"}}
      </h4>
      <p class="tio-fg-secondary">
        {{t "tuition_assistance.submitted.lpn_survey.thanks_body"}}
      </p>
    {{else}}
      <p class="tio-text-xs-medium tio-fg-muted uppercase tracking-wide mb-2">
        {{t "tuition_assistance.submitted.lpn_survey.tag_line"}}
      </p>
      <h4 id="lpn-survey-question" class="tio-text-lg-semibold mb-4">
        {{t "tuition_assistance.submitted.lpn_survey.question"}}
      </h4>
      <RadioGroup
        @value={{@selected}}
        @onChange={{@onSelect}}
        aria-labelledby="lpn-survey-question"
        as |Radio|
      >
        {{#each @options as |option|}}
          <Radio
            @value={{option.key}}
            @label={{option.label}}
            class="tio-bg-surface border tio-border-default rounded-xl px-4 py-3.5 w-full"
          />
        {{/each}}
      </RadioGroup>
      <div class="mt-4">
        <Button
          @intent="primary"
          @isRunning={{@isSubmitting}}
          @isDisabled={{not @selected}}
          {{on "click" @onSubmit}}
          data-test-lpn-survey-submit
        >
          {{t "tuition_assistance.submitted.lpn_survey.submit"}}
        </Button>
      </div>
    {{/if}}
  </div>
</template>;

export default LpnSurveyCard;
